import {DataFetcher} from '@aboutyou/data-fetcher';
import {useIsFeatureActive} from '@aboutyou/feature-flags';
import {
  SizeSelection,
  SizeSelection_Size,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/size_selection.types';
import {GetSizeSelectionResponse} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/size_selection/v1/service.types';
import {useTranslation} from '@aboutyou/i18n';
import {
  Button,
  COLORS,
  FONT_SIZES,
  Flyout,
  FlyoutBody,
  Hint,
  Z_INDEXES,
} from '@aboutyou/ui-components';
import React, {memo, useEffect, useRef, useState} from 'react';
import styled from 'styled-components';
import {ProminentBannerSectionView} from '../../backend-services/ArticleDetail/ProminentBannerSection/ProminentBannerSectionView';
import {MerchantInfoWithLegal} from '../../backend-services/MerchantInfo/MerchantInfoWithLegal';
import {useGetSizeSelection} from '../../shared-deprecated/useGetSizeSelection';
import {useIsCouponsEnabled} from '../Coupons/useIsCouponsEnabled';
import {SizeHeader} from '../SizeSelection/SizeHeader';
import {SizeSection} from '../SizeSelection/SizeSection';
import {
  SizeSectionSizeReminder,
  useSizeReminderState,
} from '../SizeSelection/SizeSectionSizeReminder';
import {usePreselectSize} from '../SizeSelection/hooks/usePreselectSize';
import {useRecommendedSize} from '../SizeSelection/hooks/useRecommendedSize';
import {SetSelectedSize} from '../SizeSelection/hooks/useSelectedSizeState';
import {useShouldDisableAddToBasketButton} from './hooks/useShouldDisableAddToBasketButton';

const MemoProminentBannerSection = memo(ProminentBannerSectionView);

export interface SharedFlyoutBaseProps {
  isOpen: boolean;
  isCaptchaRequired: boolean;
  productId: number;
  selectedSizeId?: number;
  onCancel: () => void;
  onSelect: (sizeId: SizeSelection_Size) => void;
  onAddToBasket(size?: SizeSelection_Size): Promise<void>;
  setSelectedSize?: SetSelectedSize;
  onSizeAdvisorSizeSelection?: (sizeId: number) => void;
  disableCouponsSlider?: boolean;
  renderInPortal?: boolean;
  className?: string;
  couponPromotionId?: string;
}

const usePreselectedSize = (
  sizeSelection: SizeSelection,
  sizeId?: number,
): SizeSelection_Size | undefined => {
  switch (sizeSelection.type?.$case) {
    case 'sizes':
      return sizeSelection.type.sizes.sizes.find(
        size => size.sizeId === sizeId,
      );
    case 'oneSize':
      return sizeSelection.type.oneSize.size;
    case 'sizeRuns':
      return sizeSelection.type.sizeRuns.sizeRuns[0]?.sizes.find(
        size => size.sizeId === sizeId,
      );
    case 'inlineSelection':
      return sizeSelection.type.inlineSelection.sizes.find(
        size => size.sizeId === sizeId,
      );
  }
};

export const SharedFlyoutView = ({
  selectedSizeId,
  isOpen,
  onSelect,
  product,
  saleBadges,
  brand,
  sizeSelection,
  setSelectedSize,
  onSizeAdvisorSizeSelection,
  prominentBannerSection,
  onAddToBasket,
  onCancel,
  disableCouponsSlider,
  renderInPortal,
  className,
  couponPromotionId,
}: GetSizeSelectionResponse &
  Omit<SharedFlyoutBaseProps, 'isCaptchaRequired' | 'productId'>) => {
  const recommendedSize = useRecommendedSize({
    sizeSelection,
    productId: product.id,
  });
  const isCouponsEnabled = useIsCouponsEnabled();
  const {isActive: isPriceDisplayOverhaulV2Active} = useIsFeatureActive(
    'ab_price_display_overhaul_v2',
  );

  const [isSizeReminderOpen, setIsSizeReminderOpen] = useState(false);
  const sizeSelectionType = sizeSelection.type;
  const {sizeReminderSize, showSizeReminder} = useSizeReminderState(
    sizeSelectionType?.$case === 'oneSize' && sizeSelectionType.oneSize.size
      ? [sizeSelectionType.oneSize.size.sizeId]
      : [],
  );

  usePreselectSize({
    productId: product.id,
    setSelectedSize,
    sizeSelection,
    recommendedSize,
    selectedSizeId,
    isSizeSelectionOpen: isOpen,
  });

  const preselectedSize = usePreselectedSize(sizeSelection, selectedSizeId);

  const hasAutoAddedOneSizeRef = useRef(false);

  // One-size products have no size grid, so opening the flyout is itself the
  // add action. The isOpen gate keeps consumers that mount the flyout closed
  // from adding to the basket as a mount side effect; the ref limits each
  // open to a single add (StrictMode re-runs effects in dev).
  useEffect(() => {
    if (!isOpen) {
      hasAutoAddedOneSizeRef.current = false;
      return;
    }

    if (
      hasAutoAddedOneSizeRef.current ||
      sizeSelection.type?.$case !== 'oneSize'
    ) {
      return;
    }

    hasAutoAddedOneSizeRef.current = true;
    onAddToBasket(sizeSelection.type.oneSize.size);
  }, [isOpen, sizeSelection, onAddToBasket]);

  const {t} = useTranslation();

  const handleSelect = (size: SizeSelection_Size) => {
    showSizeReminder(size);
    onSelect(size);
  };

  const shouldDisableAddToBasketButton = useShouldDisableAddToBasketButton({
    sizeSelection,
    selectedSizeId,
  });

  const isCouponSliderEnabled =
    !disableCouponsSlider &&
    isCouponsEnabled &&
    prominentBannerSection?.type?.$case === 'couponSlider';

  if (sizeSelection.type?.$case === 'oneSize') {
    return null;
  }

  return (
    <TopLayerFlyout
      open={isOpen}
      data-testid="sizeSelectionFlyout"
      onClose={onCancel}
      renderInPortal={renderInPortal}
      className={className}
      headerContent={
        <SizeHeader
          product={product}
          saleBadges={saleBadges}
          productPrice={
            isPriceDisplayOverhaulV2Active
              ? sizeSelection.priceV2
              : sizeSelection.price
          }
          selectedSizePrice={
            preselectedSize?.availability?.$case === 'inStock'
              ? preselectedSize?.availability.inStock[
                  isPriceDisplayOverhaulV2Active ? 'priceV2' : 'price'
                ]
              : undefined
          }
          disableBadges
          enableSelectYourSizeTitle
          hideStruckPrices
        />
      }
      hideCloseButton
      bodyContent={
        <>
          <SizeSection
            product={product}
            onSizeSelect={handleSelect}
            selectedSize={preselectedSize}
            sizeSelection={sizeSelection}
            onSizeAdvisorSizeSelection={onSizeAdvisorSizeSelection}
            enableRebrush
            showSizeReminder={(size: SizeSelection_Size) => {
              showSizeReminder(size);
              setIsSizeReminderOpen(true);
            }}
          />

          <SizeSectionSizeReminder
            brand={brand}
            product={product!}
            SizeReminderLayoutComponent={Flyout}
            sizeReminderSize={sizeReminderSize}
            onResetSizeReminderSize={() => {
              setIsSizeReminderOpen(false);
            }}
            sizeSelectionOpenedStateKey={undefined}
            isOpen={isSizeReminderOpen}
          />

          {preselectedSize?.availability?.$case === 'inStock' &&
            preselectedSize?.availability.inStock.lowStockHint && (
              <StyledHint
                label={preselectedSize?.availability.inStock.lowStockHint}
                weight="mediumBold"
                fontSize="px12"
              />
            )}
          {selectedSizeId && (
            <MerchantInfoWithLegal
              availability={preselectedSize?.availability}
              sizeId={selectedSizeId}
              productId={product.id}
            />
          )}
          <div>
            {isCouponSliderEnabled && (
              <MemoProminentBannerSection
                {...prominentBannerSection}
                couponVariant="coupon-slider"
                showLowStockHint
                flatBorders
                removeNegativeMarginTop
                isInMiniADPDialog={false}
                selectedCouponPromotionId={couponPromotionId}
              />
            )}
            <CtaWrapper>
              <Button
                variant="secondary"
                onClick={onCancel}
                data-testid="sizeSelectionCancel"
                size="fit-width"
              >
                {t('Cancel')}
              </Button>
              <AddButton
                onClick={() => onAddToBasket()}
                disabled={shouldDisableAddToBasketButton}
                data-testid="sizeSelectionAdd"
              >
                {t('Add')}
              </AddButton>
            </CtaWrapper>
          </div>
        </>
      }
    />
  );
};

const CtaWrapper = styled.div`
  padding: 1rem;
  display: flex;
  gap: 1rem;
  position: relative;
  z-index: ${Z_INDEXES.ADD_TO_BASKET_FLYOUT_CTA_WRAPPER};
  background-color: ${COLORS.White};
`;

const AddButton = styled(Button)`
  flex: 2;
`;

const TopLayerFlyout = styled(Flyout)`
  &&& {
    z-index: ${Z_INDEXES.GENERIC_FLYOUT};
  }

  header {
    border-bottom-color: ${COLORS.Grey4};
  }

  ${FlyoutBody} {
    padding: 0;
  }
`;

export const SharedFlyoutBase = ({
  productId,
  ...props
}: SharedFlyoutBaseProps) => {
  return (
    <DataFetcher
      request={useGetSizeSelection({
        productId,
        enableSizeFinder: true,
        isRequestDisabled: !props.isOpen,
      })}
      View={SharedFlyoutView}
      viewProps={props}
      Error={() => null}
      Loading={() => null}
    />
  );
};

const StyledHint = styled(Hint)`
  border-radius: 4px;
  padding: 6px 32px;
  width: auto;
  line-height: ${FONT_SIZES.px18};
  text-align: left;
`;
